import * as React from 'react'
import { Card as MuiCard, CardProps as MuiCardProps, useTheme } from '@mui/material'
import { useRouter } from 'next/router'
import Image from 'next/image'
import { styled } from '@mui/material/styles'

interface CardProps extends MuiCardProps {
    to?: string
    status?: 'default' | 'primary' | 'error'
    image?: string
}

const Background = styled('div')`
    width: 100%;
    height: 100%;
    opacity: 0.2;
    left: 0;
    z-index: 0;
    position: absolute;
    top: 0;
`

const Card = ({ variant = 'outlined', image, ...props }: CardProps) => {
    const router = useRouter()
    const { palette } = useTheme()

    const background = React.useMemo(() => {
        switch (props.status) {
            case 'primary':
                return palette.primary.light
            case 'error':
                return palette.error.light
            default:
                return undefined
        }
    }, [palette, props.status])

    const border = React.useMemo(() => {
        switch (props.status) {
            case 'primary':
                return palette.primary.main
            case 'error':
                return palette.error.main
            default:
                return undefined
        }
    }, [palette, props.status])

    const baseStyle = React.useMemo(() => {
        return {
            overflow: 'hidden',
            padding: '1rem',
            height: '100%',
            background: background,
            borderColor: !image ? border : 'rgba(212, 232, 103, 0.05)',
        }
    }, [image, background, border])

    if (props.to || props.onClick) {
        const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            if (props.onClick) props.onClick(event)
            if (props.to) router.push(props.to)
        }
        return (
            <MuiCard
                {...props}
                onClick={handleClick}
                variant={variant}
                style={{ ...baseStyle, ...props.style, position: 'relative' }}>
                {image ? (
                    <Background>
                        <Image alt="" layout="fill" src={image} />
                    </Background>
                ) : null}
                {props.children}
            </MuiCard>
        )
    }
    return (
        <MuiCard {...props} variant={variant} style={{ ...baseStyle, ...props.style, position: 'relative' }}>
            {image ? (
                <Background>
                    <Image alt="" layout="fill" src={image} />
                </Background>
            ) : null}
            {props.children}
        </MuiCard>
    )
}

export default Card
